import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';

import { TEXT_SDQ_REPORT } from 'constants/string/texts';
import { buildRadarRiskLineValues, getScoreFromSummary, resolveSdqThresholds } from 'utils/sdqAnalysis';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const LABEL_COLORS = ['rgba(6, 137, 17, 1)', 'rgba(6, 137, 17, 1)', 'rgba(188, 85, 0, 1)', 'rgba(34, 34, 34, 1)', 'rgba(34, 34, 34, 1)'];

const SCORE_COLOR = 'rgba(8, 105, 233, 1)';
const RISK_HIGH_COLOR = '#E53935';
const RISK_MODERATE_COLOR = '#F9A825';
const RISK_LINE_DASH = [2, 2];

/** Fixed scale used when risk lines are drawn, so the thresholds are never clipped. */
const RISK_SCALE_MAX = 10;

const labels = [
  {
    key: 'PB',
    label: 'PB（向社会的）',
  },
  {
    key: 'PP',
    label: 'PP（仲間関係）',
  },
  {
    key: 'HI',
    label: 'HI（多動／不注意）',
  },
  {
    key: 'ES',
    label: 'ES（情緒）',
  },
  {
    key: 'CP',
    label: 'CP（行為）',
  },
];

const DashedLegendLine = ({ color }) => (
  <svg width="20" height="4" className="shrink-0" aria-hidden>
    <line x1="0" y1="2" x2="20" y2="2" stroke={color} strokeWidth="2" strokeDasharray="2 2" />
  </svg>
);

const ChartLegend = () => (
  <div className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-black-102">
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="w-5 h-0.5 shrink-0 rounded-sm" style={{ backgroundColor: SCORE_COLOR }} aria-hidden />
      {TEXT_SDQ_REPORT.INDIVIDUAL_SCORE}
    </span>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <DashedLegendLine color={RISK_HIGH_COLOR} />
      {TEXT_SDQ_REPORT.RISK_HIGH_LINE}
    </span>
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <DashedLegendLine color={RISK_MODERATE_COLOR} />
      {TEXT_SDQ_REPORT.RISK_MODERATE_LINE}
    </span>
  </div>
);

/**
 * SDQ radar chart.
 *
 * `thresholds` comes from the new analysis format; when it is complete the chart
 * gains the same dashed 中／高リスク reference lines and legend the guardian app
 * shows. Reports without thresholds keep rendering exactly as before.
 */
const RadarChart = (props) => {
  const { data, thresholds } = props;

  const resolvedThresholds = resolveSdqThresholds(thresholds);
  const riskLines = resolvedThresholds ? buildRadarRiskLineValues(resolvedThresholds) : null;

  const datasets = [
    {
      data: labels.map((label) => getScoreFromSummary(data, label.key)),
      order: 2,
      backgroundColor: 'rgba(8, 105, 233, 0.5)',
      borderColor: SCORE_COLOR,
      borderWidth: 1.5,
      pointBackgroundColor: SCORE_COLOR,
      pointBorderColor: '#fff',
      pointRadius: 5,
    },
  ];

  if (riskLines) {
    datasets.push(
      {
        data: riskLines.high,
        order: 0,
        backgroundColor: 'transparent',
        borderColor: RISK_HIGH_COLOR,
        borderWidth: 1.5,
        borderDash: RISK_LINE_DASH,
        pointRadius: 0,
        fill: false,
      },
      {
        data: riskLines.moderate,
        order: 1,
        backgroundColor: 'transparent',
        borderColor: RISK_MODERATE_COLOR,
        borderWidth: 1.5,
        borderDash: RISK_LINE_DASH,
        pointRadius: 0,
        fill: false,
      }
    );
  }

  const dataChart = {
    labels: labels.map((label) => label.label),
    datasets: datasets,
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      r: {
        min: 0,
        max: riskLines ? RISK_SCALE_MAX : Math.max(0, ...Object.values(data || {})),
        pointLabels: {
          callback: function (label, index) {
            return '  ' + label + '  ';
          },
          font: {
            size: 14,
            weight: 'bold',
          },
          color: function (context) {
            return LABEL_COLORS[context.index];
          },
        },
        angleLines: {
          display: false,
        },
        grid: {
          display: false,
          circular: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="w-full h-full flex flex-col">
      {riskLines && <ChartLegend />}
      <div className="w-full flex-1 min-h-0">
        <Radar data={dataChart} options={options} />
      </div>
    </div>
  );
};

export default RadarChart;
